<template>
  <div class="floorplan-dots">
    <span
      v-for="panorama in filterFloorplanVisible"
      :key="panorama.objectId"
      class="floorplan-point"
      :class="{
        'pilot-active': pilotAnimation.indexMapping[panorama.objectId] && pilotAnimation.indexMapping[panorama.objectId] <= pilotAnimation.index,
        'active': hoveredPanorama.objectId === panorama.objectId,
      }"
      :style="{
        transform: pointPosition(panorama)
      }"
      @mouseenter="setHoveredPanorama(panorama)"
      @mouseleave="setHoveredPanorama({})"
      @click="selectPanorama(panorama)"
    >
      <span
        v-if="hoveredPanorama.objectId === panorama.objectId"
        class="floorplan-point-tag"
        :style="{
          transform: `translateX(calc(-50% + 10px)) scale(${(1 / scale) / floorplanScale})`
        }"
      >
        {{ panorama.customCategory || $t(panorama.category) }}
      </span>
    </span>
    <span
      v-if="isShowDot && currentPanorama.position"
      class="floorplan-activated-point"
      :style="{
        transform: pointPosition(currentPanorama),
        backgroundColor: activatedColor
      }"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'FloorplanDots',
  props: {
    ratio: {
      type: Number,
      default () {
        return 1
      }
    },
    ratioRfe: {
      type: Number,
      default () {
        return 1
      }
    },
    xOffset: {
      type: Number,
      default () {
        return 0
      }
    },
    yOffset: {
      type: Number,
      default () {
        return 0
      }
    },
    activatedColor: {
      type: String,
      default () {
        return ''
      }
    },
    scale: {
      type: Number,
      default () {
        return 1
      }
    },
    floorplanScale: {
      type: Number,
      default () {
        return 1
      }
    }
  },
  data () {
    return {
      pilotAnimation: {
        intervalTime: 0.3,
        restartTime: 0.7,
        index: 0,
        indexMax: 0,
        indexMapping: {},
        currentPanoramaIndex: 0,
        timeoutId: ''
      }
    }
  },
  computed: {
    ...mapGetters([
      'currentPanorama',
      'hoveredPanorama',
      'krpanoEl',
      'currentPilotData',
      'panoramas'
    ]),
    filterFloorplanVisible () {
      return this.panoramas.filter(panorama => !panorama.floorplanHidden)
    },
    isShowDot () {
      return !this.currentPanorama.floorplanHidden
    }
  },
  watch: {
    currentPilotData () {
      this.initPilotAnimation()
    },
    panoramas () {
      this.initPilotAnimation()
    },
    currentPanorama () {
      this.initPilotAnimation()
    }
  },

  methods: {
    ...mapActions([
      'setHoveredPanorama'
    ]),

    selectPanorama (panorama = {}) {
      if (panorama.objectId !== this.currentPanorama.objectId) {
        window.krpanoObj.call(`prepare_change_scene(panorama_${panorama.objectId}, ${panorama.objectId}, 'FloorplanDots');`)
        this.$emit('selectedPanorama', panorama)
      }
    },

    pointPosition (panorama) {
      const x = (panorama.position ? panorama.position.x + 12.85 / this.ratioRfe : 0) * this.ratio + this.xOffset - 10
      const y = (panorama.position ? panorama.position.y + 15.00 / this.ratioRfe : 0) * this.ratio + this.yOffset - 10
      return `translate(${x}px, ${y}px) scale(${this.scale})`
    },

    initPilotAnimation () {
      if (this.pilotAnimation.timeoutId) {
        clearTimeout(this.pilotAnimation.timeoutId)
      }
      this.pilotAnimation.timeoutId = ''
      this.pilotAnimation.indexMapping = {}
      this.pilotAnimation.index = 0
      this.pilotAnimation.currentPanoramaIndex = 0
      if (!_.isEmpty(this.currentPilotData)) {
        let index = 1
        this.currentPilotData.naviCollection.forEach(chapter => {
          if (!this.pilotAnimation.indexMapping[chapter.panoramaId]) {
            if (this.panoramas.findIndex(p => p.objectId === chapter.panoramaId) !== -1) {
              if (chapter.panoramaId === this.currentPanorama.objectId) {
                this.pilotAnimation.currentPanoramaIndex = index
              }
              this.pilotAnimation.indexMapping[chapter.panoramaId] = index
              index++
            }
          }
        })
        this.pilotAnimation.indexMax = index
      }
      if (!_.isEmpty(this.pilotAnimation.indexMapping)) {
        this.pilotAnimation.timeoutId = setTimeout(() => {
          this.startPilotAnimation(this.pilotAnimation.currentPanoramaIndex)
        }, this.pilotAnimation.intervalTime * 1000)
      }
    },

    startPilotAnimation (index = 0) {
      this.pilotAnimation.index = index
      if (index >= this.pilotAnimation.indexMax) {
        this.pilotAnimation.index = this.pilotAnimation.currentPanoramaIndex
        this.pilotAnimation.timeoutId = setTimeout(() => {
          this.startPilotAnimation(this.pilotAnimation.currentPanoramaIndex + 1)
        }, this.pilotAnimation.restartTime * 1000)
      } else {
        this.pilotAnimation.timeoutId = setTimeout(() => {
          this.startPilotAnimation(index + 1)
        }, this.pilotAnimation.intervalTime * 1000)
      }
    }
  }
}
</script>

<style lang="stylus" rel="stylesheet/stylus" scoped>
@import '~css/addon/main'
@import '~css/common/extends'

.floorplan-point {
  cursor: pointer
  background-color: alpha($white, 40%)
  box-shadow: 0 1px 10px alpha($dark-gray, .8)
  transition: transform .25s ease-out, background-color .15s ease-out

  &.pilot-active {
    background-color: #0ff
  }

  &.active, &:hover {
    background-color: $light-pink-color
    border-style: none
  }
}

.floorplan-activated-point {
  opacity: .9
  transition: transform .8s ease-out
  background-color: alpha($pink-color, .9)
  pointer-events: none
}

.floorplan-point,
.floorplan-activated-point {
  position: absolute
  display: flex
  width: 20px
  height: 20px
  border-radius: 50%
}

.floorplan-point-tag {
  @extend .text-ellipsis
  font-size: 12px
  line-height: 14px
  position: absolute
  top: 0
  left: 0
  display: inline-block
  margin-top: 25px
  vertical-align: baseline
  padding: 2.5px 5px
  border-radius: .25rem
  color: $white
  text-align: center
  background-color: rgba(68, 68, 68, .5)
  transform-origin: top
  z-index: -1
}
</style>

<style lang="stylus" rel="stylesheet/stylus">
.yc-floorplan {
  .floorplan-point {
    &.active, &:hover {
      background-color: alpha(#fc3, 60%)
    }
  }
}
</style>
